import { useEffect, useMemo, useRef } from 'react';

import { Button } from '~/modules/elements/akui/button';
import { ScrollableRow } from '~/modules/elements/akui/selector';
import { RegisterFunc } from '~/modules/hooks/use-keyboard-nav';
import scrollIntoView from '~/modules/utils/scroll-into-view';
import { SongGroup } from '~/routes/sing-a-song/song-selection/hooks/use-song-list';
import { getSongIdWithNew } from '~/routes/sing-a-song/song-selection/utils/get-song-id-with-new';

interface Props {
  groupedSongList: SongGroup[];
  onScrollToGroup: (group: SongGroup) => void;
  /** Indexes of the groups scrolled into view — see `useVisibleSongGroups`, owned by the screen. */
  visibleGroups: number[];
  keyboardNavRegister?: RegisterFunc;
  focusedSong?: string;
}

interface GroupNavItemProps {
  group: SongGroup;
  isFirstActive: boolean;
  active: boolean;
  containsFocusedSong: boolean;
  onScrollToGroup: (group: SongGroup) => void;
  'data-test': string;
  keyboardNavRegister?: RegisterFunc;
}

// GroupNavItem replicates the scroll-to-center behavior of Selector.Item manually because
// SongGroupsNavigation highlights *multiple* active groups at once (activeGroups: number[]),
// so it can't use the Selector component which is designed for a single selected value.
function GroupNavItem({
  group,
  isFirstActive,
  active,
  containsFocusedSong,
  onScrollToGroup,
  'data-test': dataTest,
  keyboardNavRegister,
}: GroupNavItemProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const keyboardNavProps = keyboardNavRegister?.(group.name, () => onScrollToGroup(group));

  useEffect(() => {
    if (isFirstActive && ref.current) {
      scrollIntoView(ref.current, { inline: 'center', block: 'nearest' });
    }
  }, [isFirstActive]);

  const keyboardFocused = keyboardNavProps?.focused ?? false;
  // A group is normally labelled with a key - a letter, "0-9", a flag - which the width bounds
  // below are cut for. Search mode labels its two groups with a phrase instead ("Search results",
  // "Unverified songs"), and a 64px cap holds about five characters of one: the rest of the label
  // hangs outside the button, so only the middle of the word sits inside the highlight. Those size
  // to their own text instead of sharing the row out.
  const phraseLabel = !group.displayShort && group.name.length > 3;
  // The group holding the focused song gets a dimmer highlight, but only as a fallback: once the
  // list is scrolled onto that group (active) or the nav row itself is being keyboard-navigated,
  // the regular full highlight wins.
  const subtleFocused = containsFocusedSong && !active && !keyboardFocused;

  return (
    <Button
      ref={ref}
      size="mini"
      onClick={() => onScrollToGroup(group)}
      {...keyboardNavProps}
      // focused must come after the spread so the active-group highlight isn't
      // overwritten by keyboardNavProps.focused (which is false when not keyboard-navigating)
      focused={active || keyboardFocused || subtleFocused}
      subtleFocused={subtleFocused}
      // Override register-provided values that should not change
      data-active={active}
      data-test={dataTest}
      // The row shares the width of the search row above it, so the keys share out whatever is
      // left over rather than stopping short of it, and never shrink below their own width, so
      // "0-9" keeps the room its three characters need. The bounds are what make that fit at both
      // ends: 36px is F1's key, small enough that twenty-seven of them and a laptop's narrower
      // toolbar still hold each other, and 64px is the cap, because a catalogue with six groups
      // would otherwise draw six slabs instead of six keys.
      className={`text-md min-w-9 shrink-0 animate-none whitespace-nowrap ${phraseLabel ? 'px-4' : 'max-w-16 grow'}`}>
      {group.displayShort ?? group.name}
    </Button>
  );
}

export default function SongGroupsNavigation({
  groupedSongList,
  onScrollToGroup,
  visibleGroups,
  keyboardNavRegister,
  focusedSong,
}: Props) {
  const firstActiveGroup = visibleGroups.length > 0 ? Math.min(...visibleGroups) : -1;

  const focusedSongGroup = useMemo(
    () =>
      focusedSong
        ? groupedSongList.findIndex((group) =>
            group.songs.some((song) => getSongIdWithNew(song, group) === focusedSong),
          )
        : -1,
    [groupedSongList, focusedSong],
  );

  return (
    <ScrollableRow className="items-center">
      {groupedSongList.map((group, index) => {
        const active = visibleGroups.includes(index);
        return (
          <GroupNavItem
            key={group.name}
            group={group}
            active={active}
            containsFocusedSong={index === focusedSongGroup}
            isFirstActive={index === firstActiveGroup}
            onScrollToGroup={onScrollToGroup}
            keyboardNavRegister={keyboardNavRegister}
            data-test={`group-navigation-${group.name}`}
          />
        );
      })}
    </ScrollableRow>
  );
}
